/**
 * authStore — 로그인/토큰/사용자 프로필 Zustand 전역 스토어.
 *
 * 【역할】
 * - JWT access/refresh 토큰 localStorage 영속화
 * - /api/auth/login/logout/me 래퍼 + Axios 인터셉터 토큰 주입 연동
 * - 성공 시 menuStore/tabStore 리셋 (로그인 경계 처리)
 *
 * 【주요 action】
 * - login(id, pw) → POST /api/auth/login → {user, menus, accessToken, refreshToken}
 * - logout() → 토큰 삭제 + menu/tab 스토어 정리 + /login 리다이렉트
 * - bootstrap() → 앱 진입 시 localStorage 토큰 로드 후 /api/auth/me 검증
 * - refresh() → 401 시 /api/auth/refresh 로 access 재발급
 *
 * 【저장 구조】
 * - user: AuthUser (userId/userName/email/roles/locale)
 * - token / refreshToken (localStorage `fw.accessToken` / `fw.refreshToken`)
 * - loaded: bootstrap 완료 플래그 (RouterGuard 가 참조)
 *
 * 【포팅 원본】 vue-spring-fw `useAuthStore.ts` (Pinia).
 */
import { create } from 'zustand'
import http from '@/framework/api/axios'
import { useMenuStore } from '@/store/menuStore'
import { useTabStore } from '@/store/tabStore'

export interface AuthUser {
  userId: string
  userName: string
  email?: string
  roles?: string[]
  locale?: string
}

interface AuthState {
  user: AuthUser | null
  token: string | null
  refreshToken: string | null
  loaded: boolean
  login: (userId: string, password: string) => Promise<boolean>
  logout: () => void
  loadUserInfo: () => Promise<void>
  refresh: () => Promise<boolean>
}

const TOKEN_KEY = 'fw.accessToken'
const REFRESH_KEY = 'fw.refreshToken'

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem(TOKEN_KEY),
  refreshToken: localStorage.getItem(REFRESH_KEY),
  loaded: false,

  login: async (loginId: string, password: string) => {
    try {
      const res = await http.post('/auth/login', { loginId, password })
      const data = res.data?.data ?? res.data ?? {}
      const { accessToken, refreshToken, user, menus } = data
      if (accessToken) {
        localStorage.setItem(TOKEN_KEY, accessToken)
        if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken)
        // 서버가 전달한 user.locale 을 localStorage 에 저장 → axios interceptor X-Locale 헤더 사용
        if (user?.locale) localStorage.setItem('fw.locale', user.locale)
        set({ token: accessToken, refreshToken, user: user ?? null, loaded: true })
        useMenuStore.getState().setTreeFromBackend(Array.isArray(menus) ? menus : [])
        // 이전 세션의 탭 흔적 제거 — 대시보드만 남긴 상태로 시작
        useTabStore.getState().resetToDashboard()
        return true
      }
      return false
    } catch {
      return false
    }
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    set({ user: null, token: null, refreshToken: null, loaded: false })
    useMenuStore.getState().reset()
    useTabStore.getState().resetToDashboard()
  },

  loadUserInfo: async () => {
    if (!get().token) return
    try {
      const res = await http.get('/auth/me')
      const payload = res.data?.data ?? res.data ?? {}
      // 본 시스템 백엔드 /auth/me 응답: {sub, loginId, name, roles}
      // → react-fastapi 표준 AuthUser 로 매핑 (userId/userName/email/roles/locale)
      const raw = payload.user ?? payload
      const user = raw
        ? {
            userId: raw.userId ?? raw.loginId ?? String(raw.sub ?? ''),
            userName: raw.userName ?? raw.name ?? raw.loginId ?? '',
            email: raw.email,
            roles: raw.roles ?? [],
            locale: raw.locale
          }
        : null
      const menus = payload.menus
      if (user?.locale) localStorage.setItem('fw.locale', user.locale)
      set({ user, loaded: true })
      if (Array.isArray(menus) && menus.length > 0) {
        useMenuStore.getState().setTreeFromBackend(menus)
      }
    } catch {
      set({ loaded: true })
    }
  },

  refresh: async () => {
    const rt = get().refreshToken
    if (!rt) return false
    try {
      const res = await http.post('/auth/refresh', { refreshToken: rt })
      const data = res.data?.data ?? res.data ?? {}
      if (data.accessToken) {
        localStorage.setItem(TOKEN_KEY, data.accessToken)
        set({ token: data.accessToken })
        return true
      }
    } catch {
      /* noop */
    }
    return false
  }
}))
